import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ColorBox = styled.div`
  width: 100%;
  height: 120px;
  background: ${(props) => props.color};
  position: relative;
`;

const CopiedBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  opacity: ${(props) => (props.show ? '1' : '0')};
  transition: opacity 0.2s;
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const InfoSection = styled.div`
  padding: 16px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff'};
`;

const ColorName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#0F172A')};
  margin-bottom: 4px;
`;

const ColorValue = styled.div`
  font-size: 12px;
  font-family: 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-bottom: 4px;
`;

const ColorUsage = styled.div`
  font-size: 11px;
  color: ${(props) => (props.theme.name === 'dark' ? '#64748B' : '#94A3B8')};
  margin-top: 8px;
  line-height: 1.4;
`;

/**
 * ColorSwatch - Display a color with name, value, and usage information
 *
 * @param {Object} props
 * @param {string} props.name - Display name of the color
 * @param {string} props.value - Hex, RGB, or RGBA color value
 * @param {string} [props.usage] - Optional usage description
 */
const ColorSwatch = ({ name, value, usage }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Container onClick={handleCopy}>
      <ColorBox color={value}>
        <CopiedBadge show={copied}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          Copied!
        </CopiedBadge>
      </ColorBox>
      <InfoSection>
        <ColorName>{name}</ColorName>
        <ColorValue>{value}</ColorValue>
        {usage && <ColorUsage>{usage}</ColorUsage>}
      </InfoSection>
    </Container>
  );
};

ColorSwatch.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  usage: PropTypes.string,
};

export default ColorSwatch;